import { useState, useEffect } from 'preact/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Github, 
  Gitlab, 
  GitBranch, 
  Star, 
  Lock, 
  Globe,
  Search,
  Download,
  ExternalLink,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Server,
  GitGitFork
} from 'lucide-preact';
import { GitIntegrationService, GitProvider, GitRepository, GitCloneRequest } from '../services/gitIntegration';

interface GitIntegrationProps {
  onProjectCreated?: (projectId: string) => void;
  onClose?: () => void;
}

export function GitIntegration({ onProjectCreated, onClose }: GitIntegrationProps) {
  const [gitService] = useState(() => new GitIntegrationService());
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [repositories, setRepositories] = useState<GitRepository[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [selectedRepo, setSelectedRepo] = useState<GitRepository | null>(null);
  const [projectName, setProjectName] = useState('');
  const [branch, setBranch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authToken, setAuthToken] = useState<string>('');
  const [error, setError] = useState<string>('');

  const providers = gitService.getProviders();

  const getProviderIcon = (providerId: string) => {
    switch (providerId) {
      case 'github': return <Github className="h-4 w-4" />;
      case 'gitlab': return <Gitlab className="h-4 w-4" />;
      case 'bitbucket': return <Server className="h-4 w-4" />;
      case 'azure-devops': return <Server className="h-4 w-4" />;
      default: return <GitBranch className="h-4 w-4" />;
    }
  };

  const handleProviderSelect = async (providerId: string) => {
    setSelectedProvider(providerId);
    setError('');
    
    try {
      setIsAuthenticating(true);
      const authResponse = await gitService.authenticateWithProvider(providerId);
      setAuthToken(authResponse.accessToken);
      
      // Fetch user repositories
      const repos = await gitService.getUserRepositories(providerId, authResponse.accessToken);
      setRepositories(repos);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Authentication failed');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !selectedProvider) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const repos = await gitService.searchRepositories(selectedProvider, searchQuery, authToken);
      setRepositories(repos);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Search failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomUrlClone = async () => {
    if (!customUrl.trim()) return;
    
    const validation = gitService.validateRepositoryUrl(customUrl);
    if (!validation.isValid) {
      setError(validation.error || 'Invalid repository URL');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const request: GitCloneRequest = {
        provider: validation.provider || 'generic',
        repositoryUrl: customUrl,
        branch: branch || undefined,
        projectName: projectName || undefined,
        authToken: authToken || undefined
      };

      const result = await gitService.cloneRepository(request);
      
      if (result.success && result.projectId) {
        onProjectCreated?.(result.projectId);
        onClose?.();
      } else {
        setError(result.error || 'Failed to clone repository');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Clone failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRepositoryClone = async (repo: GitRepository) => {
    setIsLoading(true);
    setError('');
    
    try {
      const request: GitCloneRequest = {
        provider: repo.provider,
        repositoryUrl: repo.cloneUrl,
        branch: branch || repo.defaultBranch,
        projectName: projectName || repo.name,
        authToken: authToken || undefined
      };

      const result = await gitService.cloneRepository(request);
      
      if (result.success && result.projectId) {
        onProjectCreated?.(result.projectId);
        onClose?.();
      } else {
        setError(result.error || 'Failed to clone repository');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Clone failed');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Import from Git</h2>
          <p className="text-muted-foreground">Clone repositories from GitHub, GitLab, Bitbucket, or Azure DevOps</p>
        </div>
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            <XCircle className="h-4 w-4" />
          </Button>
        )}
      </div>

      <Tabs defaultValue="providers" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="providers">Git Providers</TabsTrigger>
          <TabsTrigger value="search">Search Repositories</TabsTrigger>
          <TabsTrigger value="custom">Custom URL</TabsTrigger>
        </TabsList>

        <TabsContent value="providers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Connect to Git Provider</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {providers.map((provider) => (
                  <Button
                    key={provider.id}
                    variant={selectedProvider === provider.id ? "default" : "outline"}
                    onClick={() => handleProviderSelect(provider.id)}
                    disabled={isAuthenticating}
                    className="h-20 flex flex-col gap-2"
                  >
                    {getProviderIcon(provider.id)}
                    <span>{provider.name}</span>
                    {isAuthenticating && selectedProvider === provider.id && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {repositories.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Your Repositories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search repositories..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button onClick={handleSearch} disabled={isLoading}>
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {repositories.map((repo) => (
                      <div
                        key={repo.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium">{repo.fullName}</h3>
                            {repo.isPrivate ? (
                              <Lock className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <Globe className="h-3 w-3 text-muted-foreground" />
                            )}
                          </div>
                          {repo.description && (
                            <p className="text-sm text-muted-foreground mb-2">{repo.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Star className="h-3 w-3" />
                              {repo.stars}
                            </span>
                            <span className="flex items-center gap-1">
                              <GitFork className="h-3 w-3" />
                              {repo.forks}
                            </span>
                            <span className="flex items-center gap-1">
                              <GitBranch className="h-3 w-3" />
                              {repo.defaultBranch}
                            </span>
                            <span>Updated {formatDate(repo.lastUpdated)}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(repo.url, '_blank')}
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleRepositoryClone(repo)}
                            disabled={isLoading}
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="search" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Search Public Repositories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map((provider) => (
                        <SelectItem key={provider.id} value={provider.id}>
                          <div className="flex items-center gap-2">
                            {getProviderIcon(provider.id)}
                            {provider.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Search repositories..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="flex-1"
                  />
                  <Button onClick={handleSearch} disabled={isLoading || !selectedProvider}>
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>

                {repositories.length > 0 && (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {repositories.map((repo) => (
                      <div
                        key={repo.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium">{repo.fullName}</h3>
                            {repo.isPrivate ? (
                              <Lock className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <Globe className="h-3 w-3 text-muted-foreground" />
                            )}
                          </div>
                          {repo.description && (
                            <p className="text-sm text-muted-foreground mb-2">{repo.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Star className="h-3 w-3" />
                              {repo.stars}
                            </span>
                            <span className="flex items-center gap-1">
                              <GitFork className="h-3 w-3" />
                              {repo.forks}
                            </span>
                            <span className="flex items-center gap-1">
                              <GitBranch className="h-3 w-3" />
                              {repo.defaultBranch}
                            </span>
                            <span>Updated {formatDate(repo.lastUpdated)}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(repo.url, '_blank')}
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleRepositoryClone(repo)}
                            disabled={isLoading}
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="custom" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Clone from Custom URL</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="repo-url">Repository URL</Label>
                  <Input
                    id="repo-url"
                    placeholder="https://github.com/owner/repo.git"
                    value={customUrl}
                    onChange={(e) => setCustomUrl((e.target as HTMLInputElement).value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="project-name">Project Name (optional)</Label>
                    <Input
                      id="project-name"
                      placeholder="Auto-generated from repo name"
                      value={projectName}
                      onChange={(e) => setProjectName((e.target as HTMLInputElement).value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="branch">Branch (optional)</Label>
                    <Input
                      id="branch"
                      placeholder="main"
                      value={branch}
                      onChange={(e) => setBranch((e.target as HTMLInputElement).value)}
                    />
                  </div>
                </div>

                <Button
                  onClick={handleCustomUrlClone}
                  disabled={isLoading || !customUrl.trim()}
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Cloning...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Clone Repository
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
